import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Plus, Trash2, AlertCircle } from 'lucide-react';
import { getCommonCurrencies, getAllCurrencyNames, resolveCurrencyName } from '../../utils/currencyLookup';

const commonCurrencies = getCommonCurrencies();
const allCurrencyNames = getAllCurrencyNames();

const ReserveCurrencyNode = ({ id, data }) => {
  const { reserves = [], onUpdate } = data;
  const [newCurrency, setNewCurrency] = useState('VRSC');
  const [customCurrency, setCustomCurrency] = useState('');
  const [newWeight, setNewWeight] = useState(0.25);

  const totalWeight = reserves.reduce((sum, r) => sum + (r.weight || 0), 0);
  const isValidTotal = Math.abs(totalWeight - 1) < 0.001;

  const handleAddCurrency = () => {
    const currencyToAdd = customCurrency.trim() || newCurrency;
    if (!currencyToAdd) return;
    
    // Check if currency already exists
    if (reserves.some(r => r.currency === currencyToAdd)) {
      return;
    }

    const updatedReserves = [...reserves, { currency: currencyToAdd, weight: newWeight }];
    onUpdate?.({ reserves: updatedReserves });
    setCustomCurrency('');
    setNewWeight(0.25);
  };

  const handleRemoveCurrency = (index) => {
    const updatedReserves = reserves.filter((_, i) => i !== index);
    onUpdate?.({ reserves: updatedReserves });
  };

  const handleUpdateWeight = (index, weight) => {
    const updatedReserves = reserves.map((r, i) => 
      i === index ? { ...r, weight: parseFloat(weight) || 0 } : r
    );
    onUpdate?.({ reserves: updatedReserves });
  };

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-slate-800 rounded-xl border-2 border-amber-500/50 shadow-xl min-w-[320px] max-w-[400px]"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-t-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">Reserve Currencies</h3>
            <p className="text-white/70 text-xs">Backing currencies for basket</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Current reserves list */}
        {reserves.length > 0 && (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-slate-400">
              Added Currencies ({reserves.length})
            </label>
            <AnimatePresence>
              {reserves.map((reserve, index) => (
                <motion.div
                  key={`${reserve.currency}-${index}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white truncate block">{resolveCurrencyName(reserve.currency)}</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={reserve.weight}
                    onChange={(e) => handleUpdateWeight(index, e.target.value)}
                    className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                  <button
                    onClick={() => handleRemoveCurrency(index)}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Weight total indicator */}
            <div className={`flex items-center gap-2 text-xs ${isValidTotal ? 'text-emerald-400' : 'text-amber-400'}`}>
              {!isValidTotal && <AlertCircle className="w-3 h-3" />}
              <span>Total weight: {totalWeight.toFixed(2)} {!isValidTotal && '(must equal 1.0)'}</span>
            </div>

            {/* Visual weight bar */}
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  isValidTotal 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                    : 'bg-gradient-to-r from-amber-500 to-orange-500'
                }`}
                style={{ width: `${Math.min(totalWeight * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Add new currency section */}
        <div className="border-t border-slate-700 pt-4 space-y-3">
          <label className="block text-xs font-medium text-slate-400">
            Add Reserve Currency
          </label>
          
          <div className="space-y-2">
            {/* Common currencies quick select */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Common Currencies</label>
              <select
                value={newCurrency}
                onChange={(e) => {
                  setNewCurrency(e.target.value);
                  setCustomCurrency('');
                }}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              >
                {commonCurrencies.map((curr) => (
                  <option key={curr} value={curr}>
                    {curr}
                  </option>
                ))}
              </select>
            </div>

            {/* All currencies searchable select */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">All Currencies</label>
              <select
                value={customCurrency || ''}
                onChange={(e) => {
                  setCustomCurrency(e.target.value);
                  setNewCurrency('');
                }}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              >
                <option value="">-- Select from all currencies --</option>
                {allCurrencyNames.map((curr) => (
                  <option key={curr} value={curr}>
                    {curr}
                  </option>
                ))}
              </select>
            </div>
            
            <input
              type="text"
              value={customCurrency}
              onChange={(e) => setCustomCurrency(e.target.value)}
              placeholder="Or type custom currency name"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              Weight (0-1)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={newWeight}
              onChange={(e) => setNewWeight(parseFloat(e.target.value) || 0)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>

          <button
            onClick={handleAddCurrency}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4" />
            Add Currency
          </button>
        </div>

        {reserves.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-2">
            No reserve currencies added yet. Add at least one currency to create a basket.
          </p>
        )}
      </div>

      {/* Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-slate-800"
      />
    </motion.div>
  );
};

export default ReserveCurrencyNode;
